import React, { useState, useEffect, useRef } from 'react';
import userService from '../services/userService';
import Modal from '../components/Modal';
import authService from '../services/authService';
import { useI18n } from '../i18n/I18nProvider';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info'); // info | success | error
  // Creation form state (new user form shown for admins above table)
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role_name: 'user',
    phone_number: ''
  });
  const [createError, setCreateError] = useState('');
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [confirmState, setConfirmState] = useState({ open: false, mode: null, user: null, loading: false });
  const [hideDeleted, setHideDeleted] = useState(true);
  // Password change modal local state (isolated feedback so page header not polluted)
  const [pwModal, setPwModal] = useState({
    open: false,
    password: '',
    confirm: '',
    saving: false,
    show1: false,
    show2: false,
    error: '',
    success: ''
  });
  const pwCloseTimerRef = useRef(null);
  const restoreWidthRef = useRef(0);
  const measureRef = useRef(null);

  useEffect(() => {
    // Create hidden measure element to capture width of Save + Delete buttons combined
    if (measureRef.current) {
      const w = measureRef.current.getBoundingClientRect().width;
      if (w && w !== restoreWidthRef.current) {
        restoreWidthRef.current = w;
        // force rerender
        setUsers(u => [...u]);
      }
    }
  }, [users]);

  const fetchUsers = () => {
    userService.getUsers().then(
      (response) => {
        // Server already filters, but do an extra client-side filter for safety
        let data = response.data;
        if (currentUserRole === 'manager') {
          data = data.filter(u => u.role_name === 'user' || u.user_id === myUserIdRef.current);
        } else if (currentUserRole === 'user') {
          data = data.filter(u => u.user_id === myUserIdRef.current);
        }
        setUsers(data.map(user => ({ ...user, originalRole: user.role_name })));
      },
      (error) => {
        const resMessage =
          (error.response &&
            (error.response.data?.msg || error.response.data?.message)) ||
          error.message ||
          error.toString();
        setMessage(resMessage);
      }
    );
  };

  const myUserIdRef = React.useRef(null);
  useEffect(() => {
    const initRole = async () => {
      const stored = authService.getCurrentUser();
      if (stored?.token) {
        const decoded = authService.decodeJwt(stored.token);
        const id = decoded?.user?.id;
        if (id) myUserIdRef.current = id;
      }
      let roleSet = false;
      const stored2 = authService.getCurrentUser();
      if (stored2?.role) {
        setCurrentUserRole(stored2.role);
        roleSet = true;
      } else if (stored2?.token) {
        const decoded = authService.decodeJwt(stored2.token);
        if (decoded?.user?.role) {
          setCurrentUserRole(decoded.user.role);
          localStorage.setItem('user', JSON.stringify({ ...stored2, role: decoded.user.role }));
          roleSet = true;
        }
      }
      if (!roleSet) {
        const profile = await authService.fetchProfile();
        if (profile?.role) setCurrentUserRole(profile.role);
      }
    };
    initRole();
  }, []);

  // Refetch users when role resolved
  useEffect(() => {
    if (currentUserRole) fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserRole]);
  const resetCreateForm = () => {
    setNewUser({ first_name: '', last_name: '', email: '', password: '', role_name: 'user', phone_number: '' });
    setCreateError('');
  };

  const handleCreateSubmit = (e) => {
    e.preventDefault();
    if (creating) return;
    if (!newUser.email || !newUser.password || !newUser.first_name || !newUser.last_name) {
      setCreateError(t('users.validationMissingFields'));
      return;
    }
    setCreating(true);
    setCreateError('');
    userService.createUser({
      email: newUser.email,
      password: newUser.password,
      role: newUser.role_name,
      first_name: newUser.first_name,
      last_name: newUser.last_name,
      phone_number: newUser.phone_number || null
    }).then(
      () => {
        setMessage('User created successfully');
        setMessageType('success');
        setCreating(false);
        resetCreateForm();
        fetchUsers();
      },
      (error) => {
        const resMessage = (error.response && (error.response.data?.msg || error.response.data?.message)) || error.message || error.toString();
        setMessage(resMessage);
        setMessageType('error');
        setCreating(false);
      }
    );
  };

  const handleInputChange = (e, userId) => {
    const { name, value } = e.target;
    setUsers(users.map(user => user.user_id === userId ? { ...user, [name]: value } : user));
  };

  const handleNewFieldChange = (e) => {
    const { name, value } = e.target;
    setNewUser(n => ({ ...n, [name]: value }));
  };

  const hierarchy = { user: 1, manager: 2, admin: 3 };

  const canEditUser = (user) => {
    if (!currentUserRole) return false;
    // self always editable
    if (parseInt(user.user_id, 10) === myUserIdRef.current) return true;
    // cannot edit deleted users (need restore first by admin)
    if (user.is_deleted) return false;
    return hierarchy[currentUserRole] >= hierarchy[user.role_name];
  };

  const canChangeRole = (user) => {
    if (!canEditUser(user)) return false;
    return hierarchy[currentUserRole] > hierarchy[user.role_name] || parseInt(user.user_id, 10) === myUserIdRef.current;
  };

  const handleSave = (userId) => {
    const userToUpdate = users.find(user => user.user_id === userId);
    if (!userToUpdate) return;
    const { email, role_name, first_name, last_name, phone_number, username } = userToUpdate;

    userService.updateUser(userId, { email, role: role_name, first_name, last_name, phone_number, username }).then(
      (res) => {
        setMessage(t('users.save') + ' ✔');
        setMessageType('success');
        // If updating self, patch local storage so header updates immediately
        if (parseInt(userId, 10) === myUserIdRef.current) {
          const stored = authService.getCurrentUser();
            if (stored) {
              const updated = { ...stored, first_name: res.data?.first_name || first_name, last_name: res.data?.last_name || last_name };
              localStorage.setItem('user', JSON.stringify(updated));
              window.dispatchEvent(new CustomEvent('auth:user-updated'));
            }
        }
        fetchUsers(); // Refresh to confirm changes
      },
      (error) => {
        const resMessage =
          (error.response &&
            (error.response.data?.msg || error.response.data?.message)) ||
          error.message ||
          error.toString();
  setMessage(resMessage);
  setMessageType('error');
      }
    );
  };

  const handleSoftDelete = (userId) => {
    const user = users.find(u => u.user_id === userId);
    setConfirmState({ open: true, mode: 'delete', user, loading: false });
  };

  const handleRestore = (userId) => {
    const user = users.find(u => u.user_id === userId);
    setConfirmState({ open: true, mode: 'restore', user, loading: false });
  };

  const performConfirmed = () => {
    if (!confirmState.user || !confirmState.mode) return;
    setConfirmState(cs => ({ ...cs, loading: true }));
    const promise = confirmState.mode === 'delete'
      ? userService.softDeleteUser(confirmState.user.user_id)
      : userService.restoreUser(confirmState.user.user_id);
    promise.then(
      () => {
        setMessage(confirmState.mode === 'delete' ? 'User deleted' : 'User restored');
        setConfirmState({ open: false, mode: null, user: null, loading: false });
        fetchUsers();
      },
      (error) => {
        const resMessage = (error.response && (error.response.data?.msg || error.response.data?.message)) || error.message || error.toString();
        setMessage(resMessage);
        setConfirmState({ open: false, mode: null, user: null, loading: false });
      }
    );
  };

  const { t } = useI18n();
  const superMail = String('superadmin@unicomp.hu').toLowerCase();
  const isSuperViewer = (() => {
    try {
      const curr = authService.getCurrentUser();
      const mail = String(curr?.email || '').toLowerCase();
      const role = String(curr?.role || '').toLowerCase();
      return mail === superMail || role === 'superadmin';
    } catch { return false; }
  })();
  const openPwModal = (user) => {
    if (!user || parseInt(user.user_id, 10) !== myUserIdRef.current) return;
    // Clear any pending auto-close timers
    if (pwCloseTimerRef.current) {
      clearTimeout(pwCloseTimerRef.current);
      pwCloseTimerRef.current = null;
    }
    setPwModal({ open: true, password: '', confirm: '', saving: false, show1: false, show2: false, error: '', success: '' });
  };
  const submitPwChange = () => {
    if (pwModal.saving || pwModal.success) return;
    // Validation (local modal messages only)
    if (!pwModal.password || pwModal.password.length < 8) {
      setPwModal(m => ({ ...m, error: t('users.passwordTooShort') || 'Minimum 8 karakter', success: '' }));
      return;
    }
    if (pwModal.password !== pwModal.confirm) {
      setPwModal(m => ({ ...m, error: t('users.passwordMismatch'), success: '' }));
      return;
    }
    // Optional: enforce mix for stronger password without blocking overlay
    const hasMix = /[A-Z]/.test(pwModal.password) && /[a-z]/.test(pwModal.password) && /\d/.test(pwModal.password);
    if (!hasMix) {
      setPwModal(m => ({ ...m, error: (t('users.passwordWeak') || 'Gyenge jelszó: adj hozzá nagybetűt és számot'), success: '' }));
      return;
    }
    setPwModal(m => ({ ...m, saving: true, error: '', success: '' }));
    userService.changeOwnPassword(pwModal.password).then(
      () => {
        console.debug('[UsersPage] Password updated OK');
        setPwModal(m => ({ ...m, saving: false, success: t('users.passwordUpdated'), error: '', password: '', confirm: '' }));
        // Auto-close after short delay to show success message
        pwCloseTimerRef.current = setTimeout(() => {
          setPwModal({ open: false, password: '', confirm: '', saving: false, show1: false, show2: false, error: '', success: '' });
          pwCloseTimerRef.current = null;
        }, 1400);
      },
      (error) => {
        const resMessage = (error.response && (error.response.data?.msg || error.response.data?.message)) || error.message || error.toString();
        console.error('[UsersPage] Password update failed:', resMessage);
        setPwModal(m => ({ ...m, saving: false, error: resMessage, success: '' }));
      }
    );
  };
  return (
    <>
  <div className="sb-page">
  {message && <div className={`alert alert-${messageType === 'success' ? 'success' : messageType === 'error' ? 'danger' : 'info'}`}>{message}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '12px 0 4px' }}>
        <h4 style={{ margin: 0 }}>{t('users.users')}</h4>
      </div>
      {currentUserRole === 'admin' && (
        <form onSubmit={handleCreateSubmit} style={{ margin: '12px 0 16px', background: '#fafafa', border: '1px solid #e0e0e0', padding: '10px 12px', borderRadius: 6, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 140 }}>
            <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('users.lastName')}</label>
            <input className="form-control" name="last_name" value={newUser.last_name} onChange={handleNewFieldChange} disabled={creating} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 140 }}>
            <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('users.firstName')}</label>
            <input className="form-control" name="first_name" value={newUser.first_name} onChange={handleNewFieldChange} disabled={creating} />
          </div>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
            <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('users.email')}</label>
            <input className="form-control" type="email" name="email" value={newUser.email} onChange={handleNewFieldChange} disabled={creating} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 140 }}>
            <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('users.role')}</label>
            <select className="form-control" name="role_name" value={newUser.role_name} onChange={handleNewFieldChange} disabled={creating}>
              <option value="user">user</option>
              <option value="manager">manager</option>
              <option value="admin">admin</option>
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 160 }}>
            <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('users.phone')}</label>
            <input className="form-control" name="phone_number" value={newUser.phone_number} onChange={handleNewFieldChange} disabled={creating} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 200, position: 'relative' }}>
            <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('users.passwordPlaceholder')}</label>
            <input
              className="form-control"
              type="password"
              name="password"
              value={newUser.password}
              onChange={handleNewFieldChange}
              placeholder={t('users.passwordPlaceholder')}
              disabled={creating}
              autoComplete="new-password"
              inputMode="text"
            />
            {/* Keep strength hint inline but non-intrusive and fixed height to prevent vertical shift */}
            <div style={{ position: 'absolute', top: '100%', left: 0, fontSize: 11 }}>
              {(() => {
                const pwd = newUser.password || '';
                const longEnough = pwd.length >= 8;
                const hasMix = /[A-Z]/.test(pwd) && /[a-z]/.test(pwd) && /\d/.test(pwd);
                const strong = longEnough && hasMix;
                const color = strong ? '#2e7d32' : longEnough ? '#f9a825' : '#c62828';
                const text = strong
                  ? (t('users.passwordStrong') || 'Erős jelszó')
                  : longEnough
                    ? (t('users.passwordMedium') || 'Közepes jelszó: adj hozzá nagybetűt és számot')
                    : (t('users.passwordWeak') || 'Gyenge jelszó: minimum 8 karakter, nagybetű és szám');
                return <span style={{ color }}>{text}</span>;
              })()}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingTop: 4 }}>
            <button type="submit" className="btn btn-primary" disabled={creating} style={{ padding: '6px 14px' }}>{creating ? (t('common.saving') || '...') : (t('users.create'))}</button>
            { (createError) && <span style={{ color: '#c62828', fontSize: 12 }}>{createError}</span> }
          </div>
        </form>
      )}
      <div style={{ display:'flex', alignItems:'center', gap:18, margin:'8px 0 10px' }}>
        <label style={{ fontSize:12, display:'flex', alignItems:'center', gap:6 }}>
          <input type="checkbox" checked={hideDeleted} onChange={e=> setHideDeleted(e.target.checked)} /> {t('common.hideDeleted') || 'Töröltek elrejtése'}
        </label>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ccc' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #ddd' }}>
            {/* Show ID in first column */}
            <th style={{ padding: '8px', textAlign: 'left' }}>ID</th>
            <th style={{ padding: '8px', textAlign: 'left' }}>{t('users.lastName')}</th>
            <th style={{ padding: '8px', textAlign: 'left' }}>{t('users.firstName')}</th>
            <th style={{ padding: '8px', textAlign: 'left' }}>{t('users.email')}</th>
            <th style={{ padding: '8px', textAlign: 'left' }}>{t('users.role')}</th>
            <th style={{ padding: '8px', textAlign: 'left' }}>{t('users.phone')}</th>
            <th style={{ padding: '8px', textAlign: 'left' }}>{t('users.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {users
            .filter(u => hideDeleted ? !u.is_deleted : true)
            // Hide protected superadmin from non-super viewers
            .filter(u => isSuperViewer || String(u.email||'').toLowerCase() !== superMail)
            .map((user) => (
            <tr key={user.user_id} style={{ borderBottom: '1px solid #ddd', opacity: user.is_deleted ? 0.55 : 1, background: user.is_deleted ? 'rgba(120,0,0,0.08)' : 'transparent' }}>
              {/* ID column */}
              <td style={{ padding: '8px', fontFamily: 'monospace' }}>{user.user_id}</td>
              <td style={{ padding: '8px' }}>
                {canEditUser(user) ? (
                  <input
                    type="text"
                    name="last_name"
                    value={user.last_name || ''}
                    onChange={(e) => handleInputChange(e, user.user_id)}
                  />
                ) : (
                  <span>{user.last_name || ''}</span>
                )}
              </td>
              <td style={{ padding: '8px' }}>
                {canEditUser(user) ? (
                  <input
                    type="text"
                    name="first_name"
                    value={user.first_name || ''}
                    onChange={(e) => handleInputChange(e, user.user_id)}
                  />
                ) : (
                  <span>{user.first_name || ''}</span>
                )}
              </td>
              <td style={{ padding: '8px' }}>
                {canEditUser(user) ? (
                  <input
                    type="email"
                    name="email"
                    value={user.email}
                    onChange={(e) => handleInputChange(e, user.user_id)}
                  />
                ) : (
                  <span>{user.email}</span>
                )}
              </td>
              <td style={{ padding: '8px' }}>
                {canChangeRole(user) ? (
                  <select name="role_name" value={user.role_name} onChange={(e) => handleInputChange(e, user.user_id)} disabled={user.is_deleted}>
                    <option value="user">user</option>
                    {hierarchy[currentUserRole] >= hierarchy['manager'] && <option value="manager">manager</option>}
                    {currentUserRole === 'admin' && <option value="admin">admin</option>}
                  </select>
                ) : (
                  <span>{user.role_name}{user.is_deleted && <span style={{ marginLeft: 6, fontSize: '0.7rem', background: '#722', color: '#fff', padding: '2px 6px', borderRadius: 6 }}>{t('users.deletedBadge') || 'törölt'}</span>}</span>
                )}
              </td>
              <td style={{ padding: '8px' }}>
                {canEditUser(user) ? (
                  <input
                    type="text"
                    name="phone_number"
                    value={user.phone_number || ''}
                    onChange={(e) => handleInputChange(e, user.user_id)}
                  />
                ) : (
                  <span>{user.phone_number || ''}</span>
                )}
              </td>
              <td style={{ padding: '8px' }}>
                <>
                    {/* Hidden measurer (only render once for a representative row of active editable + deletable) */}
                    {(!user.is_deleted && canEditUser(user) && hierarchy[currentUserRole] > hierarchy[user.role_name] && parseInt(user.user_id,10) !== myUserIdRef.current) && (
                      <span ref={measureRef} className="user-restore-measure">
                        <button className="btn btn-primary" type="button" style={{ marginRight: 6 }}>{t('users.save')}</button>
                        <button className="btn btn-danger" type="button">{t('users.delete') || 'Törlés'}</button>
                      </span>
                    )}
                    {(!user.is_deleted) && canEditUser(user) && (
                      <button className="btn btn-primary" onClick={() => handleSave(user.user_id)} style={{ marginRight: 6 }}>{t('users.save')}</button>
                    )}
                    {/* Self password change button */}
                    {parseInt(user.user_id,10) === myUserIdRef.current && !user.is_deleted && (
                      <button className="btn btn-secondary" onClick={() => openPwModal(user)} style={{ marginRight: 6 }}>{t('users.changePassword')}</button>
                    )}
                    {(() => {
                      const targetMail = String(user.email||'').toLowerCase();
                      const notSelf = parseInt(user.user_id, 10) !== myUserIdRef.current;
                      const notDeleted = !user.is_deleted;
                      const notSuper = targetMail !== superMail;
                      let canDelete = false;
                      if (currentUserRole === 'admin') canDelete = true; // admin can delete admin/manager/user (except superadmin)
                      else if (currentUserRole === 'manager') canDelete = user.role_name === 'user';
                      else canDelete = false;
                      if (notDeleted && notSelf && notSuper && canDelete) {
                        return <button className="btn btn-danger" onClick={() => handleSoftDelete(user.user_id)} style={{ marginRight: 6 }}>{t('users.delete') || 'Törlés'}</button>;
                      }
                      return null;
                    })()}
                    {user.is_deleted && currentUserRole === 'admin' && (
                      <button
                        className="btn btn-secondary btn-restore-wide"
                        style={{
                          background: '#2e7d32',
                          borderColor: '#2e7d32',
                          width: restoreWidthRef.current ? restoreWidthRef.current + 'px' : undefined,
                          display: 'inline-flex',
                          justifyContent: 'center'
                        }}
                        onClick={() => handleRestore(user.user_id)}
                      >{t('users.restore') || 'Visszaállítás'}</button>
                    )}
                </>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <Modal
      open={confirmState.open}
      title={confirmState.mode === 'delete' ? t('confirm.titleDelete') : t('confirm.titleRestore')}
      onClose={() => !confirmState.loading && setConfirmState({ open: false, mode: null, user: null, loading: false })}
      actions={
        <>
          <button className="btn btn-secondary" disabled={confirmState.loading} onClick={() => setConfirmState({ open: false, mode: null, user: null, loading: false })}>{t('confirm.cancel')}</button>
          <button className={confirmState.mode === 'delete' ? 'btn btn-danger' : 'btn btn-primary'} disabled={confirmState.loading} onClick={performConfirmed}>
            {confirmState.loading ? '...' : t('confirm.proceed')}
          </button>
        </>
      }
    >
      <p style={{ marginTop: 0 }}>
        {confirmState.mode === 'delete' ? t('confirm.messageDelete') : t('confirm.messageRestore')}
      </p>
      {confirmState.user && (
        <div style={{ fontSize: '.8rem', opacity: .75, marginTop: 12 }}>
          ID: {confirmState.user.user_id} • {confirmState.user.username} ({confirmState.user.email})
        </div>
      )}
    </Modal>
    <Modal
      open={pwModal.open}
      title={t('users.changePassword')}
      disableAutoFocus={true}
      onClose={() => {
        if (pwModal.saving) return;
        if (pwCloseTimerRef.current) {
          clearTimeout(pwCloseTimerRef.current);
          pwCloseTimerRef.current = null;
        }
        setPwModal({ open:false, password:'', confirm:'', saving:false, show1:false, show2:false, error:'', success:'' });
      }}
      actions={
        <>
          <button className="btn btn-secondary" disabled={pwModal.saving} onClick={() => {
            if (pwCloseTimerRef.current) {
              clearTimeout(pwCloseTimerRef.current);
              pwCloseTimerRef.current = null;
            }
            setPwModal({ open:false, password:'', confirm:'', saving:false, show1:false, show2:false, error:'', success:'' });
          }}>{t('users.cancel')}</button>
          <button className="btn btn-primary" disabled={pwModal.saving || !!pwModal.success} onClick={submitPwChange}>{pwModal.saving ? (t('common.saving')||'...') : t('users.save')}</button>
        </>
      }
    >
      <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
        <div style={{ display:'flex', flexDirection:'column' }}>
          <label style={{ fontSize:11, fontWeight:600 }}>{t('users.newPassword')}</label>
          <div className="auth-password-wrapper" style={{ position:'relative' }}>
            <input
              data-autofocus
              type={pwModal.show1 ? 'text':'password'}
              className="form-control"
              value={pwModal.password}
              onChange={e=> setPwModal(m => ({ ...m, password: e.target.value }))}
              placeholder={t('users.newPassword')}
              disabled={pwModal.saving || !!pwModal.success}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="pw-toggle-btn"
              tabIndex={-1}
              onClick={() => setPwModal(m => ({ ...m, show1: !m.show1 }))}
              aria-label={pwModal.show1 ? 'Hide password' : 'Show password'}
            >
              {pwModal.show1 ? (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.77 21.77 0 0 1 5.06-6.94"/><path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a21.83 21.83 0 0 1-2.63 3.95"/><path d="M14.12 14.12A3 3 0 0 1 9.88 9.88"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z"/><circle cx="12" cy="12" r="3"/></svg>
              )}
            </button>
          </div>
        </div>
        <div style={{ display:'flex', flexDirection:'column' }}>
          <label style={{ fontSize:11, fontWeight:600 }}>{t('users.confirmPassword')}</label>
          <div className="auth-password-wrapper" style={{ position:'relative' }}>
            <input
              type={pwModal.show2 ? 'text':'password'}
              className="form-control"
              value={pwModal.confirm}
              onChange={e=> setPwModal(m => ({ ...m, confirm: e.target.value }))}
              placeholder={t('users.confirmPassword')}
              disabled={pwModal.saving || !!pwModal.success}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="pw-toggle-btn"
              tabIndex={-1}
              onClick={() => setPwModal(m => ({ ...m, show2: !m.show2 }))}
              aria-label={pwModal.show2 ? 'Hide password' : 'Show password'}
            >
              {pwModal.show2 ? (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.77 21.77 0 0 1 5.06-6.94"/><path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a21.83 21.83 0 0 1-2.63 3.95"/><path d="M14.12 14.12A3 3 0 0 1 9.88 9.88"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z"/><circle cx="12" cy="12" r="3"/></svg>
              )}
            </button>
          </div>
        </div>
        {(pwModal.error || pwModal.success) && (
          <div className={`alert alert-${pwModal.error ? 'danger' : 'success'}`} style={{ marginTop: 4, padding: '4px 8px', fontSize: 12 }}>
            {pwModal.error || pwModal.success}
          </div>
        )}
      </div>
    </Modal>
    </>
  );
};

export default UsersPage;